import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/24px/icon_24px_check.svg';
import businessFlameIcon from '@repo/common/assets/icons/business_home_flame.svg';
import { applyTypography, Chip } from '@repo/common';

const CARD_VARIANTS = {
  completion: {
    cardClassName: 'bg-grayscale-gray00',
    badgeClassName: 'bg-green-light02',
    businessBadgeClassName: 'bg-[rgba(126,234,201,0.4)]',
    badgeIcon: CheckIcon,
    businessBadgeIcon: CheckIcon,
    businessBadgeIconClassName: 'size-6',
    titleClassName: 'text-title1',
    chipTextColor: 'var(--text-placeholder)',
    chipBgColor: 'var(--grayscale-gray01)',
    cardBorderClassName: '',
  },
  ongoing: {
    cardClassName: 'bg-green-deep01',
    badgeClassName: 'bg-green-light02',
    badgeIcon: `${process.env.REACT_APP_ASSETS_URL}/Kpathport/icon-18px-flame.svg`,
    businessBadgeIcon: businessFlameIcon,
    businessBadgeIconClassName: 'h-[18px] w-[14.117px]',
    badgeIconClassName: 'text-green-deep04',
    titleClassName: 'text-title1',
    chipTextColor: 'var(--green-deep02)',
    chipBgColor: 'var(--green-light02)',
    cardBorderClassName: 'border-8 border-green-light03',
  },
  locked: {
    cardClassName: 'bg-[rgba(223,223,223,0.5)]',
    badgeClassName: 'bg-grayscale-gray01',
    badgeIcon: `${process.env.REACT_APP_ASSETS_URL}/Kpathport/icon-18px-flame.svg`,
    badgeIconClassName: 'text-text-placeholder',
    titleClassName: 'text-title1',
    chipTextColor: 'var(--text-disable)',
    chipBgColor: 'var(--grayscale-gray01)',
    cardBorderClassName: '',
  },
};

const StepCard = ({
  variant = 'completion',
  title = 'Cultural Explorer',
  stepLabel = 'step 01',
  badgeIconClassName,
  onClick,
  layout = 'default',
  description,
  footer,
  interactive = true,
  className = '',
}) => {
  const navigate = useNavigate();
  const LevelTypo = applyTypography('b12Strong');
  const styles = CARD_VARIANTS[variant] || CARD_VARIANTS.completion;
  const isBusinessLayout = layout === 'business';
  const ResolvedBadgeIcon =
    isBusinessLayout && styles.businessBadgeIcon
      ? styles.businessBadgeIcon
      : styles.badgeIcon;
  const resolvedBadgeIconClassName =
    badgeIconClassName ??
    (isBusinessLayout
      ? styles.businessBadgeIconClassName
      : styles.badgeIconClassName) ??
    '';

  const chipStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px 10px',
    gap: '10px',
    backgroundColor: styles.chipBgColor,
    color: styles.chipTextColor,
    borderRadius: '30px',
    border: 'none',
  };

  const mobileTitleTypo = applyTypography('b18Strong');
  const desktopTitleTypo = applyTypography('title1');

  // ongoing 카드는 280x320, 나머지는 280x280 (모바일)
  // tabletH부터는 모두 flex로 반응형 조정 (min-w-[280px] max-w-[308px], aspect-[300/300])
  const isOngoing = variant === 'ongoing';

  // step 번호 추출 (예: "step 01" -> "01")
  const stepNumber = stepLabel.replace(/step\s*/i, '').trim();

  const handleClick = () => {
    if (!interactive || variant === 'locked') {
      return; // locked 카드는 클릭 불가
    }

    if (onClick) {
      onClick();
    } else {
      // step01(=Culture)만 워크샵 목록으로 이동
      if (stepNumber === '01') {
        navigate('/community/kpathport/culture');
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`flex flex-col items-start justify-between gap-0 overflow-clip relative rounded-[40px] tabletH:rounded-[50px] tabletV:rounded-[50px] ${
        styles.cardClassName
      } ${styles.cardBorderClassName} ${
        isBusinessLayout && isOngoing ? 'tabletH:border-[12px]' : ''
      } ${className} border-solid
                  ${
                    interactive && variant !== 'locked'
                      ? 'cursor-pointer hover:opacity-90 transition-opacity'
                      : variant === 'locked'
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-default'
                  }
                  ${
                    isBusinessLayout
                      ? 'shrink-0 size-[240px] px-[25px] py-[22px] tabletH:flex-1 tabletH:min-w-[280px] tabletH:max-w-[308px] tabletH:size-auto tabletH:aspect-square tabletH:p-[30px]'
                      : isOngoing
                      ? 'flex-1 min-w-[280px] tabletH:flex-1 tabletH:min-w-[280px] tabletH:max-w-[308px] aspect-[280/320] tabletH:aspect-[300/300] tabletH:min-h-[280px] tabletH:max-h-[308px] pt-6 px-6 pb-10 tabletH:p-[30px] tabletH:pb-[60px]'
                      : 'min-w-[280px] tabletH:flex-1 tabletH:min-w-[280px] tabletH:max-w-[308px] h-[280px] tabletH:aspect-[300/300] tabletH:min-h-[280px] tabletH:max-h-[308px] p-6 tabletH:p-[30px] tabletH:pb-[60px]'
                  }`}
    >
      <div className={isBusinessLayout ? 'flex w-full justify-end' : ''}>
        <div
          className={`flex items-center justify-center overflow-clip relative rounded-[100px] shrink-0 ${
            isBusinessLayout ? 'size-10' : 'size-8 tabletH:size-[40px] tabletV:size-[40px]'
          } ${
            isBusinessLayout && styles.businessBadgeClassName
              ? styles.businessBadgeClassName
              : styles.badgeClassName
          }`}
        >
          {ResolvedBadgeIcon ? (
            typeof ResolvedBadgeIcon === 'string' ? (
              <img
                src={ResolvedBadgeIcon}
                alt=''
                aria-hidden='true'
                className={`${
                  isBusinessLayout
                    ? ''
                    : 'h-[18px] w-[18px] tabletH:size-6 tabletV:size-6'
                } ${resolvedBadgeIconClassName}`}
              />
            ) : (
              <ResolvedBadgeIcon
                aria-hidden='true'
                className={`${
                  isBusinessLayout
                    ? ''
                    : 'h-[18px] w-[18px] tabletH:size-6 tabletV:size-6'
                } ${resolvedBadgeIconClassName} [&_path]:fill-current`}
              />
            )
          ) : (
            <span className='block' aria-hidden='true' />
          )}
        </div>
      </div>
      <div
        className={`flex flex-col items-start w-full h-auto p-0 ${
          isBusinessLayout
            ? 'gap-3 tabletH:gap-2'
            : isOngoing
            ? 'gap-3 tabletH:gap-[12px] tabletV:gap-[12px]'
            : 'gap-1.5 tabletH:gap-[12px] tabletV:gap-[12px]'
        }`}
      >
        {isBusinessLayout ? (
          <>
            <div className='hidden tabletH:block'>
              <Chip
                type='fill'
                state='disabled'
                hideIcon
                style={chipStyle}
              >
                <span className={LevelTypo}>{stepLabel}</span>
              </Chip>
            </div>
            <div className='flex w-full flex-col items-start gap-1 tabletH:contents'>
              <div className='flex w-full flex-col items-start text-title1'>
                {title}
              </div>
              {description ? (
                <p className='m-0 typo-krCaption text-green-deep03'>
                  {description}
                </p>
              ) : null}
            </div>
            {footer}
          </>
        ) : (
          <>
            <Chip type='fill' state='disabled' hideIcon style={chipStyle}>
              <span className={LevelTypo}>{stepLabel}</span>
            </Chip>
            <span
              className={`${
                isOngoing ? desktopTitleTypo : mobileTitleTypo
              } tabletH:hidden tabletV:hidden ${styles.titleClassName}`}
            >
              {title}
            </span>
            <span
              className={`hidden tabletH:block tabletV:block ${desktopTitleTypo} ${styles.titleClassName}`}
            >
              {title}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export const CompletionCard = (props) => (
  <StepCard {...props} variant='completion' />
);
export const OngoingCard = (props) => <StepCard {...props} variant='ongoing' />;
export const LockedCard = (props) => <StepCard {...props} variant='locked' />;

export default StepCard;
